/** The two pickers of the score form: what reviewers answer (three cards),
 * and a scale's points (1–3, 1–5, 1–10). */

import type { ReactNode } from "react";
import { ListChecks, SlidersHorizontal, ThumbsUp } from "lucide-react";
import type { ScoreKind } from "./types";

const KINDS: Array<{ kind: ScoreKind; title: string; note: string; icon: ReactNode }> = [
  {
    kind: "pass_fail",
    title: "Pass / fail",
    note: "Right or wrong",
    icon: <ThumbsUp size={15} strokeWidth={2} />,
  },
  {
    kind: "scale",
    title: "Scale",
    note: "A number, like 1 to 5",
    icon: <SlidersHorizontal size={15} strokeWidth={2} />,
  },
  {
    kind: "categories",
    title: "Categories",
    note: "One label from a list",
    icon: <ListChecks size={15} strokeWidth={2} />,
  },
];

const POINTS = [3, 5, 10];

export function KindCards({ value, onChange }: { value: ScoreKind; onChange: (kind: ScoreKind) => void }) {
  return (
    <div role="radiogroup" aria-label="Answer type" className="grid grid-cols-3 gap-2">
      {KINDS.map((k) => (
        <label
          key={k.kind}
          className={`flex cursor-pointer flex-col gap-1.5 rounded-[9px] border p-3 transition-[border-color,background-color] has-focus-visible:outline-2 has-focus-visible:outline-link ${
            value === k.kind
              ? "border-link bg-run-bg/40 shadow-[0_0_0_1px_var(--color-link)]"
              : "border-line hover:bg-subtle"
          }`}
        >
          <input
            type="radio"
            name="score-kind"
            className="sr-only"
            checked={value === k.kind}
            onChange={() => onChange(k.kind)}
          />
          <span
            aria-hidden
            className="flex size-7 items-center justify-center rounded-md bg-chip text-chip-ink"
          >
            {k.icon}
          </span>
          <span className="text-[13px] font-semibold text-ink">{k.title}</span>
          <span className="text-xs text-muted">{k.note}</span>
        </label>
      ))}
    </div>
  );
}

export function Points({ value, onChange }: { value: number; onChange: (max: number) => void }) {
  return (
    <div role="radiogroup" aria-label="Points" className="inline-flex rounded-lg border border-input p-0.5">
      {POINTS.map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          onClick={() => onChange(n)}
          className={`rounded-md px-3 py-1 font-mono text-[13px] ${
            value === n ? "bg-primary text-on-primary" : "text-body hover:bg-hover"
          }`}
        >
          1–{n}
        </button>
      ))}
    </div>
  );
}
